import React, { useState, useEffect } from 'react';
import { apiClient } from '../../lib/api';
import { AdminUserMetaData, AdminUsersListResponse, AuthMethodInfo, CreateUserRequest, CreateUserResponse, UpdateUserRequest, UpdateUserResponse, Pagination } from '@df-gate-share/shared';
import MessageDialog from '../../components/MessageDialog';
import { useMessageDialog } from '../../hooks/useMessageDialog';

type StatusFilter = 'all' | 'active' | 'suspended';
interface DialogParam {
    show: boolean;
    type: 'disable' | 'reset' | null;
    user: AdminUserMetaData | null;
}

const formatAuthMethods = (authMethods?: AuthMethodInfo[]): React.ReactNode => {
  if (!authMethods || authMethods.length === 0) {
    return <span className="badge badge--info">メール</span>;
  }
  return (
    <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
      {authMethods.map((method, index) => {
        if (method.type === 'email') {
          return (
            <span key={index} className="badge badge--info">メール</span>
          );
        }
        const providerLabel = method.provider === 'google' ? 'Google'
          : method.provider === 'microsoft' ? 'Microsoft'
          : method.provider === 'dfgate' ? 'DFGate'
          : 'SSO';
        return (
          <span key={index} className="badge badge--active">SSO ({providerLabel})</span>
        );
      })}
    </div>
  );
};

const UserManagement: React.FC = () => {
  const { dialogState, closeDialog, showAlert } = useMessageDialog();
  const [users, setUsers] = useState<AdminUserMetaData[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('active');
  const [emailFilter, setEmailFilter] = useState('');
  const [pagination, setPagination] = useState<Pagination>({
    hasMore: false,
    count: 0
  });
  const [currentCursor, setCurrentCursor] = useState<string | undefined>(undefined);
  const [cursorHistory, setCursorHistory] = useState<(string | undefined)[]>([]);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingUser, setEditingUser] = useState<AdminUserMetaData | null>(null);
  const [editForm, setEditForm] = useState<UpdateUserRequest>({ name: '', role: 'user' });
  const [showConfirmDialog, setShowConfirmDialog] = useState<DialogParam>({ show: false, type: null, user: null });
  // CR-045: 確定系操作の二重送信ガード
  const [isSubmitting, setIsSubmitting] = useState(false);

  // New user form
  const [newUser, setNewUser] = useState({
    email: '',
    name: '',
    role: 'user' as 'admin' | 'user',
  } as CreateUserRequest);

  useEffect(() => {
    fetchUsers();
  }, [statusFilter]);

  const fetchUsers = async (cursor?: string, resetHistory: boolean = false) => {
    setLoading(true);
    setError('');

    try {
      const params = new URLSearchParams();
      if (statusFilter !== 'all') {
        params.append('status', statusFilter);
      }
      if (emailFilter) {
        params.append('email', emailFilter);
      }
      if (cursor) params.append('cursor', cursor);
      params.append('limit', '50');

      const response = await apiClient.get(`/api/v1/admin/users?${params.toString()}`);
      const data: AdminUsersListResponse = response.data;
      setUsers(data.users || []);
      setPagination(data.pagination || { hasMore: false, count: 0 });

      if (resetHistory) {
        setCursorHistory([]);
      }
    } catch (err) {
      setError('ユーザー一覧の取得に失敗しました');
    } finally {
      setLoading(false);
    }
  };

  const handleNextPage = () => {
    if (pagination.hasMore && pagination.nextCursor) {
      setCursorHistory([...cursorHistory, currentCursor]);
      setCurrentCursor(pagination.nextCursor);
      fetchUsers(pagination.nextCursor);
    }
  };

  const handlePrevPage = () => {
    if (cursorHistory.length > 0) {
      const newHistory = [...cursorHistory];
      const prevCursor = newHistory.pop();
      setCursorHistory(newHistory);
      setCurrentCursor(prevCursor);
      fetchUsers(prevCursor);
    }
  };

  const handleSearch = () => {
    setCurrentCursor(undefined);
    setCursorHistory([]);
    fetchUsers(undefined, true);
  };

  const handleAddUser = async () => {
    if (isSubmitting) return;
    try {
      setIsSubmitting(true);
      const response = await apiClient.post('/api/v1/admin/users', newUser);
      const data: CreateUserResponse = response.data;
      setShowAddModal(false);
      setNewUser({ email: '', name: '', role: 'user' });
      fetchUsers();
      showAlert(
        'success',
        'ユーザー作成完了',
        `ユーザー「${data.user.email}」を作成しました。\n初期パスワードを本人のメールアドレスに送信しました。`
      );
    } catch (err) {
      showAlert('error', 'エラー', 'ユーザーの作成に失敗しました');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleOpenEditModal = (user: AdminUserMetaData) => {
    setEditingUser(user);
    setEditForm({ name: user.name || '', role: user.role });
    setShowEditModal(true);
  };

  const handleUpdateUser = async () => {
    if (!editingUser || isSubmitting) return;
    try {
      setIsSubmitting(true);
      await apiClient.put(`/api/v1/admin/users/${editingUser.userId}`, editForm as UpdateUserRequest);
      setShowEditModal(false);
      setEditingUser(null);
      fetchUsers();
      showAlert('success', '更新完了', 'ユーザー情報を更新しました');
    } catch (err) {
      showAlert('error', 'エラー', 'ユーザー情報の更新に失敗しました');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDisableUser = async (user: AdminUserMetaData) => {
    if (isSubmitting) return;
    try {
      setIsSubmitting(true);
      await apiClient.put(`/api/v1/admin/users/${user.userId}/disable`);
      setShowConfirmDialog({ show: false, type: null, user: null });
      fetchUsers();
      showAlert('success', '完了', `ユーザー「${user.email}」を無効化しました`);
    } catch (err) {
      showAlert('error', 'エラー', 'ユーザーの無効化に失敗しました');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleResetPassword = async (user: AdminUserMetaData) => {
    if (isSubmitting) return;
    try {
      setIsSubmitting(true);
      await apiClient.post(`/api/v1/admin/users/${user.userId}/reset-password`);
      setShowConfirmDialog({ show: false, type: null, user: null });
      showAlert(
        'success',
        'パスワードリセット完了',
        `ユーザー「${user.email}」のパスワードをリセットしました。\n新しい一時パスワードを本人のメールアドレスに送信しました。`
      );
    } catch (err) {
      showAlert('error', 'エラー', 'パスワードのリセットに失敗しました');
    } finally {
      setIsSubmitting(false);
    }
  };

  const formatDate = (dateString?: string): string => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('ja-JP', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="card card__body">
      {/* Header */}
      <div className="flex justify-between items-center mb-8">
        <h1 className="page-title">ユーザー管理</h1>
        <button
          onClick={() => setShowAddModal(true)}
          className="btn btn--primary"
        >
          + ユーザーを追加
        </button>
      </div>

      {/* Filters */}
      <div className="filter-bar mb-6">
        <div>
          <label className="form-label">ステータス</label>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as StatusFilter)}
            className="form-select"
            style={{ minWidth: '150px' }}
          >
            <option value="all">すべて</option>
            <option value="active">有効</option>
            <option value="suspended">無効</option>
          </select>
        </div>

        <div>
          <label className="form-label">メールアドレス</label>
          <div className="flex gap-2">
            <input
              type="email"
              value={emailFilter}
              onChange={(e) => setEmailFilter(e.target.value)}
              placeholder="例: user@example.com"
              className="form-input"
              style={{ minWidth: '250px' }}
            />
            <button
              onClick={handleSearch}
              className="btn btn--success btn--sm"
            >
              検索
            </button>
          </div>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="alert alert--error">{error}</div>
      )}

      {/* Loading */}
      {loading ? (
        <div className="text-center text-muted" style={{ padding: 'var(--space-10)' }}>
          読み込み中...
        </div>
      ) : (
        <>
          {/* Users Table */}
          <div className="table-wrapper">
            <table className="table">
              <thead>
                <tr>
                  <th>メールアドレス</th>
                  <th>名前</th>
                  <th>ロール</th>
                  <th>認証方法</th>
                  <th>ステータス</th>
                  <th>作成日時</th>
                  <th>最終ログイン</th>
                  <th style={{ textAlign: 'center' }}>操作</th>
                </tr>
              </thead>
              <tbody>
                {users.map((user) => (
                  <tr key={user.userId}>
                    <td style={{ color: 'var(--color-text-primary)' }}>{user.email}</td>
                    <td style={{ color: 'var(--color-text-primary)' }}>{user.name || '-'}</td>
                    <td>
                      <span className={`badge ${user.role === 'admin' ? 'badge--admin' : 'badge--info'}`}>
                        {user.role === 'admin' ? '管理者' : '一般'}
                      </span>
                    </td>
                    <td>
                      {formatAuthMethods(user.authMethods)}
                    </td>
                    <td>
                      <span className={`badge ${user.status === 'active' ? 'badge--active' : 'badge--danger'}`}>
                        {user.status === 'active' ? '有効' : '無効'}
                      </span>
                    </td>
                    <td>{formatDate(user.createdAt)}</td>
                    <td>{formatDate(user.lastLoginAt)}</td>
                    <td style={{ textAlign: 'center' }}>
                      <div className="flex flex-col gap-1 items-center" style={{ minWidth: '90px' }}>
                        <button
                          onClick={() => handleOpenEditModal(user)}
                          className="btn btn--sm btn--outline-success"
                          style={{ width: '100%' }}
                          title="ユーザー情報を編集"
                        >
                          <span className="material-symbols-outlined icon-sm">edit</span> 編集
                        </button>
                        <button
                          onClick={() => setShowConfirmDialog({ show: true, type: 'reset', user })}
                          className="btn btn--sm btn--primary"
                          style={{ width: '100%' }}
                          title="パスワードをリセット"
                        >
                          <span className="material-symbols-outlined icon-sm">key</span> リセット
                        </button>
                        {user.status === 'active' && (
                          <button
                            onClick={() => setShowConfirmDialog({ show: true, type: 'disable', user })}
                            className="btn btn--sm btn--danger"
                            style={{ width: '100%' }}
                            title="ユーザーを無効化"
                          >
                            <span className="material-symbols-outlined icon-sm">block</span> 無効化
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {users.length === 0 && (
              <div className="empty-state">
                <div className="empty-state__desc">ユーザーが見つかりません</div>
              </div>
            )}
          </div>

          {/* Pagination Controls */}
          {users.length > 0 && (
            <div className="pagination">
              <div className="pagination__info">
                {pagination.count}件の結果を表示中
              </div>
              <div className="pagination__buttons">
                <button
                  onClick={handlePrevPage}
                  disabled={cursorHistory.length === 0}
                  className="btn btn--primary btn--sm"
                >
                  ← 前へ
                </button>
                <button
                  onClick={handleNextPage}
                  disabled={!pagination.hasMore}
                  className="btn btn--primary btn--sm"
                >
                  次へ →
                </button>
              </div>
            </div>
          )}
        </>
      )}

      {/* Add User Modal */}
      {showAddModal && (
        <div className="modal-overlay">
          <div className="modal">
            <h2 className="modal__title">新規ユーザー追加</h2>

            <div className="form-group">
              <label className="form-label form-label--required">メールアドレス</label>
              <input
                type="email"
                value={newUser.email}
                onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
                placeholder="user@example.com"
                className="form-input"
              />
            </div>

            <div className="form-group">
              <label className="form-label form-label--required">名前</label>
              <input
                type="text"
                value={newUser.name}
                onChange={(e) => setNewUser({ ...newUser, name: e.target.value })}
                placeholder="山田 太郎"
                className="form-input"
              />
            </div>

            <div className="form-group">
              <div className="form-hint">
                ※ 初期パスワードは自動生成され、登録したメールアドレスに送信されます。
                <br />
                ※ ユーザーは初回ログイン時にパスワードの変更が必要です。
              </div>
            </div>

            <div className="form-group">
              <label className="form-label form-label--required">ロール</label>
              <select
                value={newUser.role}
                onChange={(e) => setNewUser({ ...newUser, role: e.target.value as 'admin' | 'user' })}
                className="form-select"
              >
                <option value="user">一般ユーザー</option>
                <option value="admin">管理者</option>
              </select>
            </div>

            <div className="modal__actions">
              <button
                onClick={() => {
                  setShowAddModal(false);
                  setNewUser({ email: '', name: '', role: 'user' });
                }}
                className="btn btn--secondary"
              >
                キャンセル
              </button>
              <button
                onClick={handleAddUser}
                disabled={!newUser.email || !newUser.name || isSubmitting}
                className="btn btn--primary"
              >
                {isSubmitting ? '追加中...' : '追加'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit User Modal */}
      {showEditModal && editingUser && (
        <div className="modal-overlay">
          <div className="modal">
            <h2 className="modal__title">ユーザー情報の編集</h2>
            <p className="text-muted mb-4" style={{ fontSize: 'var(--text-sm)' }}>
              {editingUser.email}
            </p>

            <div className="form-group">
              <label className="form-label">名前</label>
              <input
                type="text"
                value={editForm.name || ''}
                onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                placeholder="山田 太郎"
                className="form-input"
              />
            </div>

            <div className="form-group">
              <label className="form-label">ロール</label>
              <select
                value={editForm.role}
                onChange={(e) => setEditForm({ ...editForm, role: e.target.value as 'admin' | 'user' })}
                className="form-select"
              >
                <option value="user">一般ユーザー</option>
                <option value="admin">管理者</option>
              </select>
            </div>

            <div className="modal__actions">
              <button
                onClick={() => {
                  setShowEditModal(false);
                  setEditingUser(null);
                }}
                className="btn btn--secondary"
              >
                キャンセル
              </button>
              <button
                onClick={handleUpdateUser}
                disabled={isSubmitting}
                className="btn btn--primary"
              >
                {isSubmitting ? '更新中...' : '更新'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Confirmation Dialog */}
      {showConfirmDialog.show && showConfirmDialog.user && (
        <div className="modal-overlay">
          <div className="modal" role="dialog">
            <h2 className="modal__title">
              {showConfirmDialog.type === 'disable' ? 'ユーザーの無効化' : 'パスワードのリセット'}
            </h2>
            <p className="text-muted mb-6" style={{ fontSize: 'var(--text-base)' }}>
              {showConfirmDialog.type === 'disable'
                ? `ユーザー「${showConfirmDialog.user.email}」を無効化してもよろしいですか？無効化されたユーザーはログインできなくなります。`
                : `ユーザー「${showConfirmDialog.user.email}」のパスワードをリセットしてもよろしいですか？新しい一時パスワードが生成され、本人のメールアドレスに送信されます。`
              }
            </p>

            <div className="modal__actions">
              <button
                onClick={() => setShowConfirmDialog({ show: false, type: null, user: null })}
                className="btn btn--secondary"
              >
                キャンセル
              </button>
              <button
                onClick={() => {
                  if (showConfirmDialog.type === 'disable' && showConfirmDialog.user) {
                    handleDisableUser(showConfirmDialog.user);
                  } else if (showConfirmDialog.type === 'reset' && showConfirmDialog.user) {
                    handleResetPassword(showConfirmDialog.user);
                  }
                }}
                disabled={isSubmitting}
                className={`btn ${showConfirmDialog.type === 'disable' ? 'btn--danger' : 'btn--primary'}`}
              >
                {isSubmitting
                  ? '処理中...'
                  : showConfirmDialog.type === 'disable' ? '無効化する' : 'リセットする'}
              </button>
            </div>
          </div>
        </div>
      )}

      <MessageDialog {...dialogState} onClose={closeDialog} />
    </div>
  );
};

export default UserManagement;
